import quoteServices, { type PricingPayload } from '../../services/oneButtonServices/quoteServices';
import { countIncludedModules, estimateLayoutAnnualKwh } from './preContractModuleLayout';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';

export type PreContractPricingOffer = {
    id: string;
    partner: string;
    productType: string;
    financeMethod: string;
    monthly: number;
    isCash: boolean;
    term: number | null;
    apr: number | null;
    error?: string;
};

/** Partner card with one line per selected product. */
export type PreContractPartnerPricingCard = {
    partner: string;
    lines: PreContractPricingOffer[];
    /** Sum of line amounts (monthly for financed, total for Cash). */
    total: number;
    isCash: boolean;
    /** Selected products with no priced line for this partner. */
    missingProducts: string[];
};

export type PreContractPricingResult = {
    offers: PreContractPricingOffer[];
    partnerCards: PreContractPartnerPricingCard[];
    selectedProducts: string[];
    payload: PricingPayload;
};

function parseUsageKwh(raw: string): number {
    const n = Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Prefer editable system usage; fall back to layout estimate. */
export function preContractSystemUsageKwh(form: PreContractIntakeForm): number {
    const fromForm = parseUsageKwh(form.systemUsageKwh || '');
    if (fromForm > 0) return fromForm;
    const estimated = estimateLayoutAnnualKwh({
        layouts: form.roofModuleLayouts,
        roofSelections: form.roofSelections,
        moduleWattage: form.moduleTypeWattage,
    });
    return estimated != null && estimated > 0 ? estimated : 0;
}

function activeProducts(form: PreContractIntakeForm): string[] {
    const order: PreContractProduct[] = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
    return order.filter((p) => form.products[p]);
}

function normalizePartner(partner: string): string {
    const lower = (partner || '').toLowerCase();
    if (lower.includes('good') && lower.includes('leap')) return 'GoodLeap';
    if (lower.includes('light') && lower.includes('reach')) return 'LightReach';
    if (lower === 'igs' || lower === 'ics') return 'IGS';
    if (lower.includes('enfin')) return 'Enfin';
    if (lower.includes('cash')) return 'Cash';
    return partner || 'Unknown';
}

function pricingOfferDetailsMonthly(offer: any): number {
    const details =
        offer?.pricingOfferDetails ??
        offer?.PricingOfferDetails ??
        offer?.payload?.pricingOfferDetails ??
        offer?.payload?.PricingOfferDetails;
    if (!Array.isArray(details) || !details[0]) return 0;
    const monthly =
        details[0]?.monthlyPayment ??
        details[0]?.MonthlyPayment ??
        details[0]?.totalMonthlyPayment ??
        details[0]?.TotalMonthlyPayment;
    return typeof monthly === 'number' && Number.isFinite(monthly) ? monthly : 0;
}

function monthlyFromOffer(offer: any, partnerNorm: string): number {
    if (offer?.__error) return 0;
    const payload = offer?.payload;

    if (Array.isArray(payload) && payload[0]) {
        const calc = payload[0]?.calculations?.[0] ?? payload[0]?.Calculations?.[0];
        const v =
            calc?.roundedAmount?.value ??
            calc?.RoundedAmount?.Value ??
            calc?.amount?.value ??
            calc?.Amount?.Value;
        if (typeof v === 'number') return v;
    }

    if (payload && !Array.isArray(payload)) {
        const firstPricing = payload.pricing?.[0] ?? payload.Pricing?.[0];
        const firstYear =
            firstPricing?.firstYearMonthlyPayment?.value ??
            firstPricing?.FirstYearMonthlyPayment?.Value;
        if (typeof firstYear === 'number') return firstYear;

        const firstMp = payload.monthlyPayments?.[0] ?? payload.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;

        const directVal =
            payload?.totalMonthlyPayment ??
            payload?.TotalMonthlyPayment ??
            payload?.monthlyPayment ??
            payload?.MonthlyPayment;
        if (typeof directVal === 'number') return directVal;
    }

    if (partnerNorm.includes('lightreach')) {
        const firstMp = offer?.monthlyPayments?.[0] ?? offer?.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;
    }

    if (partnerNorm.includes('enfin')) {
        const monthly = pricingOfferDetailsMonthly(offer);
        if (monthly > 0) return monthly;
    }

    const est = offer?.estimatedMonthlyPayment ?? offer?.EstimatedMonthlyPayment;
    return typeof est === 'number' ? est : 0;
}

function termAprFromOffer(offer: any): { term: number | null; apr: number | null } {
    const excel = offer?.ExcelOffer ?? offer?.excelOffer ?? {};
    const termRaw = excel?.Term ?? excel?.term ?? offer?.term ?? offer?.Term;
    const aprRaw = excel?.APR ?? excel?.apr ?? offer?.apr ?? offer?.APR;
    const term = Number(termRaw);
    const apr = Number(aprRaw);
    return {
        term: Number.isFinite(term) && term > 0 ? term : null,
        apr: Number.isFinite(apr) ? apr : null,
    };
}

/** System size (kW) from included modules × wattage. */
export function preContractSystemSizeKw(form: PreContractIntakeForm): number {
    const wattage = form.moduleTypeWattage;
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return 0;
    const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
    if (count <= 0) return 0;
    return Math.round(((count * wattage) / 1000) * 1000) / 1000;
}

export type PreContractPricingInputStatus = {
    key: string;
    label: string;
    value: string;
    ok: boolean;
};

function effectiveRoofSquares(form: PreContractIntakeForm): { low: number; normal: number; total: number } {
    const low = Math.round(form.roofSlopeLowOverride ?? form.roofSlopeLowTotal ?? 0);
    const normal = Math.round(form.roofSlopeNormalOverride ?? form.roofSlopeNormalTotal ?? 0);
    return { low, normal, total: low + normal };
}

/** Snapshot of Pricing API inputs and which ones are still missing. */
export function getPreContractPricingInputStatus(
    form: PreContractIntakeForm,
): {
    inputs: PreContractPricingInputStatus[];
    missing: string[];
    ready: boolean;
} {
    const state = (form.installState || '').trim().toUpperCase();
    const utilityCompanyCode = (form.utilityCompany || '').trim();
    const systemUsageKwh = preContractSystemUsageKwh(form);
    const systemSize = preContractSystemSizeKw(form);
    const products = activeProducts(form);
    const roofs = effectiveRoofSquares(form);
    const needsSolar = products.some((p) => p.toLowerCase().startsWith('solar')) || products.length === 0;
    const needsRoof = products.some((p) => {
        const lower = p.toLowerCase();
        return lower === 'roof' || lower === 'rr' || lower === 'rrr';
    });

    const inputs: PreContractPricingInputStatus[] = [
        {
            key: 'state',
            label: 'State',
            value: state || '—',
            ok: !!state,
        },
        {
            key: 'utilityCompanyCode',
            label: 'Utility company code',
            value: utilityCompanyCode || '—',
            ok: !!utilityCompanyCode,
        },
        {
            key: 'estimatedAnnualProduction',
            label: 'Estimated annual production (System Usage)',
            value: systemUsageKwh > 0 ? `${systemUsageKwh.toLocaleString()} kWh` : '—',
            ok: !needsSolar || systemUsageKwh > 0,
        },
        {
            key: 'systemSize',
            label: 'System size',
            value: systemSize > 0 ? `${systemSize.toFixed(2)} kW` : '—',
            ok: !needsSolar || systemSize > 0,
        },
        {
            key: 'products',
            label: 'Products',
            value: products.length > 0 ? products.join(', ') : '—',
            ok: products.length > 0,
        },
        {
            key: 'roofSquares',
            label: 'Roof squares (Low / Normal)',
            value: `${roofs.low} / ${roofs.normal}`,
            ok: !needsRoof || roofs.total > 0,
        },
        {
            key: 'batteryCount',
            label: 'Battery count',
            value: form.products.Battery ? String(Math.max(0, Math.round(form.batteryQuantity || 0))) : '0',
            ok: !form.products.Battery || (form.batteryQuantity || 0) > 0,
        },
        {
            key: 'moduleType',
            label: 'Module type / wattage',
            value:
                form.moduleTypeName && form.moduleTypeWattage
                    ? `${form.moduleTypeName} (${form.moduleTypeWattage} W)`
                    : '—',
            ok: !needsSolar || (!!form.moduleTypeWattage && form.moduleTypeWattage > 0),
        },
    ];

    const missing = inputs.filter((i) => !i.ok).map((i) => i.label);
    return { inputs, missing, ready: missing.length === 0 };
}

/**
 * Build Pricing API payload for pre-contract.
 * Uses System Usage as estimatedAnnualProduction.
 */
export function buildPreContractPricingPayload(
    form: PreContractIntakeForm,
): PricingPayload | null {
    const { ready } = getPreContractPricingInputStatus(form);
    if (!ready) return null;

    const state = (form.installState || '').trim().toUpperCase();
    const systemUsageKwh = preContractSystemUsageKwh(form);
    const systemSize = preContractSystemSizeKw(form);
    const products = activeProducts(form);
    const utilityCompanyCode = (form.utilityCompany || '').trim();
    const roofs = effectiveRoofSquares(form);
    const roofPitch =
        roofs.normal > 0 && roofs.low === 0
            ? 'Normal Slope'
            : roofs.low > 0
              ? 'Low Slope'
              : 'Normal Slope';

    return {
        state,
        systemSize,
        estimatedAnnualProduction: Math.round(systemUsageKwh),
        utilityCompanyCode,
        batteryCount: form.products.Battery ? Math.max(0, Math.round(form.batteryQuantity || 0)) : 0,
        roofPitch,
        lowRoof: roofs.low,
        standardRoof: roofs.normal,
        products: products.length > 0 ? products : ['Solar'],
    };
}

/** Map API product type onto a selected product key when possible. */
function matchSelectedProduct(productType: string, selected: string[]): string | null {
    const pt = String(productType || '')
        .trim()
        .toLowerCase();
    if (!pt) return null;

    const find = (pred: (sel: string) => boolean) => selected.find(pred) ?? null;

    if (pt.includes('solar')) return find((s) => s.toLowerCase() === 'solar');
    if (pt.includes('battery') || pt.includes('storage')) {
        return find((s) => s.toLowerCase() === 'battery');
    }
    if (pt === 'rrr' || pt.includes('roof replacement')) {
        return find((s) => s.toLowerCase() === 'rrr');
    }
    if (pt === 'rr' || pt.includes('roof repair')) {
        return find((s) => s.toLowerCase() === 'rr');
    }
    if (pt === 'roof' || pt.includes('roof')) {
        return find((s) => s.toLowerCase() === 'roof');
    }
    return find((s) => s.toLowerCase() === pt);
}

function offerSortKey(productType: string): number {
    const order = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];
    const matched = order.findIndex(
        (p) => p.toLowerCase() === productType.toLowerCase() || productType.toLowerCase().includes(p.toLowerCase()),
    );
    return matched >= 0 ? matched : 99;
}

export function parsePreContractPricingOffers(
    apiData: any,
    selectedProducts: string[] = [],
): PreContractPricingOffer[] {
    const raw = (apiData?.data ?? apiData) as Array<{
        FinancePartner?: string;
        pricing?: Array<{
            ProductType?: string;
            FinanceMethod?: string;
            financeMethodName?: string;
            Offer?: any[];
        }>;
    }>;
    if (!Array.isArray(raw)) return [];

    const selected =
        selectedProducts.length > 0
            ? selectedProducts
            : (['Solar'] as string[]);

    const offers: PreContractPricingOffer[] = [];
    raw.forEach((block) => {
        const partner = normalizePartner(block.FinancePartner || '');
        const partnerNorm = partner.toLowerCase();
        (block.pricing || []).forEach((pricingItem) => {
            const productTypeRaw = String(pricingItem.ProductType || '').trim() || 'Solar';
            const matchedProduct = matchSelectedProduct(productTypeRaw, selected);
            // Only surface offers for products the customer selected.
            if (!matchedProduct) return;

            const recommended =
                (pricingItem.Offer || []).find((o) => o?.Recommended === true) ??
                (pricingItem.Offer || [])[0];
            if (!recommended) return;
            if (recommended.__error) {
                offers.push({
                    id: `${partner}-${matchedProduct}-err`,
                    partner,
                    productType: matchedProduct,
                    financeMethod: String(pricingItem.financeMethodName || pricingItem.FinanceMethod || ''),
                    monthly: 0,
                    isCash: partner === 'Cash',
                    term: null,
                    apr: null,
                    error: String(recommended.__error),
                });
                return;
            }
            const isCash = partner === 'Cash' || (pricingItem.FinanceMethod || '').toUpperCase() === 'CASH';
            const monthly = isCash
                ? (typeof recommended.totalCost === 'number' ? recommended.totalCost : 0)
                : monthlyFromOffer(recommended, partnerNorm);
            const { term, apr } = termAprFromOffer(recommended);
            offers.push({
                id: `${partner}-${matchedProduct}-${pricingItem.FinanceMethod || 'offer'}`,
                partner,
                productType: matchedProduct,
                financeMethod: String(pricingItem.financeMethodName || pricingItem.FinanceMethod || ''),
                monthly: Number(monthly) || 0,
                isCash,
                term,
                apr,
            });
        });
    });

    // One recommended offer per partner × selected product (prefer Solar-shaped ids already matched).
    const byPartnerProduct = new Map<string, PreContractPricingOffer>();
    offers.forEach((offer) => {
        if (offer.error) return;
        const key = `${offer.partner}::${offer.productType}`;
        const existing = byPartnerProduct.get(key);
        if (!existing || (existing.monthly <= 0 && offer.monthly > 0)) {
            byPartnerProduct.set(key, offer);
        }
    });

    return Array.from(byPartnerProduct.values()).sort((a, b) => {
        const partnerCmp = a.partner.localeCompare(b.partner);
        if (partnerCmp !== 0) return partnerCmp;
        return offerSortKey(a.productType) - offerSortKey(b.productType);
    });
}

export function buildPreContractPartnerPricingCards(
    offers: PreContractPricingOffer[],
    selectedProducts: string[],
): PreContractPartnerPricingCard[] {
    const selected = selectedProducts.length > 0 ? selectedProducts : ['Solar'];
    const byPartner = new Map<string, PreContractPricingOffer[]>();

    offers.forEach((offer) => {
        if (offer.error) return;
        const list = byPartner.get(offer.partner) ?? [];
        list.push(offer);
        byPartner.set(offer.partner, list);
    });

    const cards: PreContractPartnerPricingCard[] = [];
    byPartner.forEach((lines, partner) => {
        const sorted = [...lines].sort(
            (a, b) => offerSortKey(a.productType) - offerSortKey(b.productType),
        );
        const pricedTypes = new Set(sorted.map((l) => l.productType));
        const missingProducts = selected.filter((p) => !pricedTypes.has(p));
        const isCash = sorted.every((l) => l.isCash) || partner === 'Cash';
        const total = sorted.reduce((sum, line) => sum + (Number(line.monthly) || 0), 0);
        cards.push({
            partner,
            lines: sorted,
            total,
            isCash,
            missingProducts,
        });
    });

    return cards.sort((a, b) => {
        if (a.total > 0 && b.total <= 0) return -1;
        if (b.total > 0 && a.total <= 0) return 1;
        return a.partner.localeCompare(b.partner);
    });
}

export async function fetchPreContractPricing(
    form: PreContractIntakeForm,
): Promise<PreContractPricingResult> {
    const status = getPreContractPricingInputStatus(form);
    const payload = buildPreContractPricingPayload(form);
    if (!payload) {
        throw new Error(
            status.missing.length > 0
                ? `Missing Pricing inputs: ${status.missing.join(', ')}`
                : 'Pricing inputs are incomplete.',
        );
    }
    const selectedProducts = activeProducts(form);
    const res = await quoteServices.getPricing(payload);
    const offers = parsePreContractPricingOffers(res?.data, selectedProducts);
    return {
        payload,
        selectedProducts,
        offers,
        partnerCards: buildPreContractPartnerPricingCards(offers, selectedProducts),
    };
}
